/**
 * A stable id for this browser, so "captured by this device" means something.
 *
 * The image library lives on the server, which is what stops a lost phone
 * taking a week of field work with it. But a rep with twenty shots from this
 * morning does not want yesterday's other phone mixed in, so the device is
 * recorded at capture and offered as a FILTER.
 *
 * Deliberately not a fingerprint. This is a random value the browser stores for
 * itself, it identifies nobody, and losing it costs only the ability to filter:
 * the images are still there, still the user's, and still reachable by turning
 * the filter off.
 */

const STORAGE_KEY = 'mdt.deviceId'

/** RFC 4122 v4, falling back where `crypto.randomUUID` is missing. */
const newId = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}
	// Safari only gained randomUUID in 15.4, and this app is used on phones
	// that may be older. Math.random is fine: this needs to be unique among a
	// handful of a person's own devices, not unguessable.
	return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * This device's id, creating and storing one on first use.
 *
 * Returns an in-memory id when storage is unavailable (private browsing throws
 * rather than returning null). That id will not survive a reload, so the filter
 * degrades to "everything" rather than the capture failing. Losing a filter is
 * not worth losing a photograph over.
 */
export const getDeviceId = (): string => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY)
		if (stored) return stored

		const id = newId()
		localStorage.setItem(STORAGE_KEY, id)
		return id
	} catch {
		return newId()
	}
}

export default getDeviceId
